import type { Root } from './prototypeTypes';

/**
 * Projects the mounted audio element onto Home Recent controls. This module
 * deliberately does not retain state: the media element stays the sole truth.
 */
export interface HomeRecentAudioPlaybackState {
	topicId?: string;
	chapterId?: string;
	chapterIndex?: number;
	/** The playing chapter's title when the intent knows it (optimistic card relabel). */
	chapterTitle?: string;
	/** Current mounted-media position. Undefined keeps the card's server seed. */
	progressPercent?: number;
	paused: boolean;
	ended?: boolean;
}

const playIcon = '<svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>';
const pauseIcon = '<svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>';

export function syncHomeRecentPlaybackControls(root: Root, playback: HomeRecentAudioPlaybackState | null): void {
	// `Document` is not installed as a global in the isolated JSDOM tests.
	// nodeType preserves the same realm-safe distinction in the app as well.
	const scope = root.nodeType === 9 ? root : root.ownerDocument ?? document;
	const active = playback && !playback.ended ? playback : null;
	// The element keeps existing after a natural end, but no session is live
	// any more. The ended detail still identifies its card, so the rail can
	// close that chapter's ring at 100% instead of freezing at whatever the
	// last timeupdate painted.
	const ended = playback?.ended ? playback : null;
	const buttons = Array.from(scope.querySelectorAll<HTMLButtonElement>('[data-proto-click="playHomeRecentChapter()"]'));
	const entries = buttons.map((button) => {
		const card = button.closest<HTMLElement>('.recent-card');
		const topicSlug = card?.dataset.homeRecentTopicSlug ?? '';
		const chapterIndex = Number(card?.dataset.homeRecentChapterIndex);
		const sameChapter = active?.chapterId
			? card?.dataset.homeRecentChapterId === active.chapterId
			: chapterIndex === active?.chapterIndex;
		const exact = Boolean(active && topicSlug && topicSlug === active.topicId && sameChapter);
		return { button, card, topicSlug, chapterIndex, exact };
	});
	// The finished-chapter jump plays a chapter that has no rail card, so no
	// card can match by chapter identity. The topic's single card then carries
	// the playing state instead of freezing on a dead button.
	const matchedTopics = new Set(entries.filter((entry) => entry.exact).map((entry) => entry.topicSlug));
	const endedMatchedTopics = new Set(entries
		.filter(({ card, chapterIndex }) => {
			if (!ended || !card || card.dataset.homeRecentTopicSlug !== ended.topicId) return false;
			return ended.chapterId ? card.dataset.homeRecentChapterId === ended.chapterId : chapterIndex === ended.chapterIndex;
		})
		.map((entry) => entry.topicSlug));
	for (const { button, card, topicSlug, chapterIndex, exact } of entries) {
		const topicTitle = card?.dataset.homeRecentTopicTitle || topicSlug || 'topic';
		let cardChapterTitle = card?.dataset.homeRecentChapterTitle || `Chapter ${card?.dataset.homeRecentChapterIndex ?? ''}`.trim();
		const isCurrent = Boolean(active && topicSlug && topicSlug === active.topicId
			&& (exact || !matchedTopics.has(topicSlug)));
		// Optimistic relabel: a play intent (or jump) targets a chapter the
		// server row does not describe yet. Flip the card's chapter identity to
		// the playing one so the rail reads truth immediately instead of
		// waiting for the write→reconcile round-trip.
		if (isCurrent && card && typeof active!.chapterIndex === 'number'
			&& active!.chapterTitle && String(active!.chapterIndex) !== card.dataset.homeRecentChapterIndex) {
			card.dataset.homeRecentChapterIndex = String(active!.chapterIndex);
			if (active!.chapterId) card.dataset.homeRecentChapterId = active!.chapterId;
			card.dataset.homeRecentChapterTitle = active!.chapterTitle;
			cardChapterTitle = active!.chapterTitle;
			const label = card.querySelector<HTMLElement>('.recent-info h5');
			if (label) label.textContent = `Ch.${active!.chapterIndex} · ${active!.chapterTitle}`;
		}
		const isPlaying = isCurrent && !active!.paused;
		// After a relabel the dataset is the live identity; stale snapshots must
		// not leak into the accessible name or the ended-chapter match.
		const currentChapterIndex = Number(card?.dataset.homeRecentChapterIndex);
		button.classList.toggle('is-current', isCurrent);
		button.classList.toggle('is-playing', isPlaying);
		button.setAttribute('aria-pressed', String(isPlaying));
		button.title = isPlaying ? 'Pause' : 'Play';
		button.setAttribute('aria-label', `${isPlaying ? 'Pause' : 'Play'} ${topicTitle}: Ch.${Number.isFinite(currentChapterIndex) ? currentChapterIndex : ''} · ${cardChapterTitle}`.trim());
		button.innerHTML = isPlaying ? pauseIcon : playIcon;
		if (ended && card && card.dataset.homeRecentTopicSlug === ended.topicId) {
			const endedChapterMatch = ended.chapterId
				? card.dataset.homeRecentChapterId === ended.chapterId
				: currentChapterIndex === ended.chapterIndex;
			// Close the ended chapter's own card; when that chapter has no rail
			// card (restart jump), fall back to the topic's single card.
			if (endedChapterMatch || !endedMatchedTopics.has(ended.topicId ?? '')) {
				card.dataset.homeRecentProgressPercent = '100';
				card.querySelector<HTMLElement>('.recent-thumb-progress')?.style.setProperty('--audio-progress', '100%');
				continue;
			}
		}
		if (!isCurrent || !card || typeof active?.progressPercent !== 'number') continue;
		const progress = Math.min(100, Math.max(0, active.progressPercent));
		card.dataset.homeRecentProgressPercent = String(progress);
		card.querySelector<HTMLElement>('.recent-thumb-progress')?.style.setProperty('--audio-progress', `${progress}%`);
	}
}
